<template>
  <div class="contact-modal-content">
    <van-nav-bar :title="`记录与 ${librarian.name} 的联系`" class="modal-navbar">
      <template #left>
        <van-icon name="cross" size="20" @click="$emit('close')" />
      </template>
    </van-nav-bar>

    <van-cell-group inset style="margin: 15px 0;">
      <template v-if="displayPhoneNumbers.length > 0">
        <van-cell
          v-for="(phoneInfo, index) in displayPhoneNumbers"
          :key="index"
          center
          :title="`电话 ${displayPhoneNumbers.length > 1 ? index + 1 : ''}: ${phoneInfo.original}`"
          class="phone-cell-item"
        >
          <template #right-icon>
            <div class="phone-actions">
              <van-button
                v-if="phoneInfo.cleanedForDial"
                icon="phone-o"
                type="primary"
                size="small"
                round
                @click="handleDialAction(phoneInfo.cleanedForDial, 'direct')"
                class="action-icon-button dial-button"
                :disabled="isDtmfPlaying || isEstimatingDuration"
                title="拨打电话"
              />
              <van-button
                v-if="phoneInfo.cleanedForDtmf"
                icon="volume-o"
                type="info"
                size="small"
                round
                @click="handleDialAction(phoneInfo.cleanedForDtmf, 'dtmf', phoneInfo.original)"
                :loading="isDtmfPlaying && dtmfPlayingForOriginal === phoneInfo.original"
                :disabled="isDtmfPlaying || isEstimatingDuration"
                class="action-icon-button dtmf-button"
                style="margin-left: 10px;"
                title="音频拨号"
              >
                <template #loading>
                  <van-loading type="spinner" size="16px" />
                </template>
              </van-button>
            </div>
          </template>
        </van-cell>
      </template>
      <van-cell v-else title="电话: 未记录" class="phone-cell-item"></van-cell>
    </van-cell-group>
    <div v-if="isDtmfPlaying" class="dtmf-playing-notice">
        <van-icon name="volume-o" /> 正在播放音频拨号音调... 请将听筒靠近扬声器。
    </div>
    <div v-if="contactStartTime && !isEstimatingDuration && !isDtmfPlaying" class="contact-timer-notice">
        计时已开始...
    </div>
     <div v-if="isEstimatingDuration" class="contact-timer-notice estimating">
        <van-loading type="spinner" size="16px" color="#1989fa" style="margin-right: 5px;" /> 正在估算时长...
    </div>

    <van-form @submit="onSubmit" class="contact-form">
      <van-cell-group inset class="form-cell-group">
        <van-field
          v-model.number="formData.call_duration_minutes"
          name="call_duration_minutes"
          label="通话时长(分)"
          type="number"
          placeholder="请输入或点击估算"
          :rules="[{ required: true, message: '请填写通话时长' }, { validator: durationValidator, message: '通话时长必须为非负整数' }]"
          label-width="7em"
        >
          <template #button>
            <van-button size="small" type="primary" @click="estimateDuration" :disabled="isDtmfPlaying || isEstimatingDuration || !!props.editingLog" plain>
              估算时长
            </van-button>
          </template>
        </van-field>
        <van-field
          v-model="formData.notes"
          name="notes"
          label="联系情况"
          type="textarea"
          rows="3"
          autosize
          maxlength="100"
          show-word-limit
          placeholder="请输入联系情况 (100字以内)"
          label-width="7em"
        />
        <van-field name="health_status_recorded" label="健康状况" :rules="[{ required: true, message: '请选择健康状况' }]" label-width="7em">
          <template #input>
            <van-radio-group v-model="formData.health_status_recorded" direction="horizontal" class="health-radio-group">
              <van-radio name="健康">健康</van-radio>
              <van-radio name="生病" style="margin-left: 20px;">生病</van-radio>
            </van-radio-group>
          </template>
        </van-field>
      </van-cell-group>
      <div style="margin: 30px 16px;">
        <van-button round block type="primary" native-type="submit" :loading="submitLoading" class="submit-button">
          {{ props.editingLog ? '保存修改' : '提交记录' }}
        </van-button>
      </div>
    </van-form>
  </div>
</template>

<script setup>
import { reactive, defineProps, defineEmits, onMounted, ref, computed, watch, onUnmounted } from 'vue';
import { showNotify } from 'vant';

const props = defineProps({
  librarian: {
    type: Object,
    required: true,
  },
  editingLog: { type: Object, default: null },
});
const emit = defineEmits(['submit', 'close']);

const submitLoading = ref(false);

const formData = reactive({
  librarian_id: null,
  call_duration_minutes: null,
  notes: '',
  health_status_recorded: '健康',
});

// --- 电话号码解析与清理逻辑 ---
const cleanPhoneNumberForDialing = (rawNumStr) => {
    if (!rawNumStr) return null;
    let cleaned = rawNumStr.replace(/[^\d+]/g, (match, offset) => {
        return (match === '+' && offset === 0) ? '+' : '';
    });
    return cleaned && cleaned.replace(/^\+/, '').length >= 7 ? cleaned : null;
};
const cleanAndPreparePhoneNumberForDtmf = (rawNumStr) => {
    const originalText = rawNumStr.trim();
    if (!originalText) return { original: originalText, cleanedForDtmf: null };
    let numericCandidate = originalText.replace(/\s+/g, '');
    const phoneLikePattern = /(\+?[\d*#-]{7,})/;
    const match = numericCandidate.match(phoneLikePattern);
    numericCandidate = match ? match[0] : '';
    let dtmfPlayableNumber = numericCandidate;
    const numberToCheckForAreaCode = numericCandidate.startsWith('+') ? numericCandidate.substring(1) : numericCandidate;
    if (numberToCheckForAreaCode.startsWith('0591')) {
        const restOfNumber = numberToCheckForAreaCode.substring(4);
        if (restOfNumber.replace(/[^\d*#]/g, '').length >= 7) {
            dtmfPlayableNumber = restOfNumber;
            if (process.env.NODE_ENV === 'development') {
                console.log(`DTMF Prep: Removed '0591' (or '+XX0591') from '${numericCandidate}', result for DTMF: '${dtmfPlayableNumber}'`);
            }
        } else {
             if (process.env.NODE_ENV === 'development') {
                console.log(`DTMF Prep: '${numericCandidate}' starts with '0591' but rest is too short, using original (cleaned) for DTMF.`);
             }
        }
    }
    dtmfPlayableNumber = dtmfPlayableNumber.replace(/[^\d*#]/g, '');
    const hasDtmfChars = dtmfPlayableNumber.length > 0;
    return {
      original: originalText,
      cleanedForDtmf: hasDtmfChars ? dtmfPlayableNumber : null,
    };
};
const displayPhoneNumbers = computed(() => {
  if (props.librarian && props.librarian.phone) {
    const rawSegments = props.librarian.phone.split(/[,;、\s\n\r（）()【】[\]]+/);
    return rawSegments
      .map(segment => {
        const original = segment.trim();
        if (!original) return null;
        const dtmfInfo = cleanAndPreparePhoneNumberForDtmf(original);
        return {
          original: original,
          cleanedForDial: cleanPhoneNumberForDialing(original),
          cleanedForDtmf: dtmfInfo.cleanedForDtmf,
        };
      })
      .filter(phoneInfo => phoneInfo !== null && phoneInfo.original.length > 0);
  }
  return [];
});

// --- 联系时长估算相关 ---
const contactStartTime = ref(null);
const isEstimatingDuration = ref(false);

// --- 拨号和DTMF逻辑 ---
const recentlyDialedPhoneNumber = ref(null); // 移到这里，与 handleDialAction 更近
const isDtmfPlaying = ref(false);
const dtmfPlayingForOriginal = ref('');
let audioContext = null;
let dtmfGainNode = null;
const dtmfFrequencies = {
  '1': [697, 1209], '2': [697, 1336], '3': [697, 1477],
  '4': [770, 1209], '5': [770, 1336], '6': [770, 1477],
  '7': [852, 1209], '8': [852, 1336], '9': [852, 1477],
  '*': [941, 1209], '0': [941, 1336], '#': [941, 1477],
};

// --- 生命周期钩子 ---
onMounted(() => {
  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: Mounted. Initial formData:', JSON.parse(JSON.stringify(formData)));
  }
  if (props.librarian) {
    formData.librarian_id = props.librarian.id;
  }

  isEstimatingDuration.value = false; // 重置估算状态
  submitLoading.value = false;       // 确保每次打开时重置提交加载状态
  recentlyDialedPhoneNumber.value = null; // 重置记录的拨打号码

  if (props.editingLog) {
    // 编辑模式
    if (process.env.NODE_ENV === 'development') console.log('ContactModal: Editing mode, prefilling form with:', props.editingLog);
    formData.call_duration_minutes = props.editingLog.call_duration_minutes;
    formData.notes = props.editingLog.notes;
    formData.health_status_recorded = props.editingLog.health_status_recorded;
    // 编辑模式下，不启动新的通话计时，contactStartTime 保持 null
    contactStartTime.value = null;
    // recentlyDialedPhoneNumber 可以是上次记录的，但通常编辑时不让修改这个
    // recentlyDialedPhoneNumber.value = props.editingLog.contacted_phone_number;
  } else {
    // 创建模式，自动开始计时
    contactStartTime.value = new Date();
    if (process.env.NODE_ENV === 'development') {
      console.log('ContactModal: Contact timer started on mount at', contactStartTime.value);
    }
  }
});

onUnmounted(() => {
    if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().then(() => {
            if (process.env.NODE_ENV === 'development') console.log("AudioContext closed on component unmount.");
        });
        audioContext = null;
    }
});

// --- 方法 ---
// 观察 formData.health_status_recorded 的变化
watch(() => formData.health_status_recorded, (newValue, oldValue) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`ContactModal: health_status_recorded changed from '${oldValue}' to '${newValue}'`);
  }
});

const updateContactStartTime = () => {
  contactStartTime.value = new Date();
  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: Contact start time UPDATED by dial/DTMF action at', contactStartTime.value);
  }
  showNotify({ type: 'success', message: '通话计时开始', duration: 1500 });
};

const estimateDuration = () => {
  if (props.editingLog) {
    showNotify({type: 'info', message: '编辑模式下请直接修改时长'});
    return;
  }
  if (!contactStartTime.value) {
    showNotify({ type: 'danger', message: '错误：计时器未启动，请重新打开此界面' });
    return;
  }
  isEstimatingDuration.value = true;
  const endTime = new Date();
  const diffMs = endTime.getTime() - contactStartTime.value.getTime();
  const diffMinutes = Math.ceil(diffMs / (1000 * 60));
  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: Estimating duration. Current Start Time:', contactStartTime.value, 'End:', endTime, 'Diff (min):', diffMinutes);
  }
  setTimeout(() => {
    formData.call_duration_minutes = diffMinutes >= 0 ? diffMinutes : 0;
    showNotify({ type: 'success', message: `估算时长: ${formData.call_duration_minutes} 分钟`, duration: 2000});
    isEstimatingDuration.value = false;
  }, 300);
};

const handleDialAction = (phoneNumber, type, originalUiMarker = null) => {
  if (props.editingLog) {
    showNotify({type: 'info', message: '编辑模式下无法发起新呼叫'});
    return;
  }
  updateContactStartTime();
  recentlyDialedPhoneNumber.value = phoneNumber;
  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: Recorded recently dialed number for submission:', recentlyDialedPhoneNumber.value);
  }
  if (type === 'direct') {
    dialPhoneNumber(phoneNumber);
  } else if (type === 'dtmf') {
    playCompleteDtmfSequence(phoneNumber, originalUiMarker || phoneNumber );
  }
};

const dialPhoneNumber = (phoneToDial) => {
  if (phoneToDial) {
    window.location.href = `tel:${phoneToDial}`;
  } else {
    showNotify({ type: 'warning', message: '无效的电话号码，无法拨打' });
  }
};

const initAudioContext = () => {
  if (!audioContext || audioContext.state === 'closed') {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
      dtmfGainNode = audioContext.createGain();
      dtmfGainNode.gain.value = 0.35;
      dtmfGainNode.connect(audioContext.destination);
      if (process.env.NODE_ENV === 'development') console.log("AudioContext initialized for DTMF.");
    } catch (e) {
      console.error("Failed to create AudioContext for DTMF", e);
      showNotify({type: 'danger', message: '无法初始化DTMF音频'});
      audioContext = null; return false;
    }
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume().then(() => {
        if (process.env.NODE_ENV === 'development') console.log("AudioContext resumed.");
    }).catch(err => {
        console.error("Failed to resume AudioContext:", err);
        showNotify({type: 'danger', message: '无法激活DTMF音频'});
    });
  }
  return true;
};

const playSingleDtmfTone = (char, duration = 300) => {
  return new Promise((resolve, reject) => {
    if (!initAudioContext()) {
        reject(new Error("AudioContext not available"));
        return;
    }
    if (!dtmfFrequencies[char]) {
        console.warn(`DTMF tone for '${char}' not defined.`);
        resolve();
        return;
    }
    const freqs = dtmfFrequencies[char];
    const osc1 = audioContext.createOscillator();
    const osc2 = audioContext.createOscillator();
    osc1.type = 'sine'; osc2.type = 'sine';
    osc1.frequency.value = freqs[0]; osc2.frequency.value = freqs[1];
    osc1.connect(dtmfGainNode); osc2.connect(dtmfGainNode);
    const now = audioContext.currentTime;
    osc1.start(now); osc2.start(now);
    osc1.stop(now + duration / 1000); osc2.stop(now + duration / 1000);
    let resolved = false;
    const resolveOnce = () => { if (!resolved) { resolved = true; resolve(); } };
    let endedCount = 0;
    const checkEnded = () => {
        endedCount++;
        if (endedCount === 2) resolveOnce();
    };
    osc1.onended = () => { try { osc1.disconnect(); } catch(e) { /* 忽略错误 */ } checkEnded(); };
    osc2.onended = () => { try { osc2.disconnect(); } catch(e) { /* 忽略错误 */ } checkEnded(); };
    setTimeout(resolveOnce, duration + 150);
  });
};

const playCompleteDtmfSequence = async (dtmfSequenceToPlay, originalNumUiMarker) => {
  if (isDtmfPlaying.value) { showNotify({type: 'warning', message: '正在播放音频，请稍候...'}); return; }
  if (!dtmfSequenceToPlay) { showNotify({type: 'warning', message: '号码中无可播放的音频拨号字符'}); return; }
  if (!initAudioContext()) return;
  isDtmfPlaying.value = true; dtmfPlayingForOriginal.value = originalNumUiMarker;
  const finalSequenceForDisplay = `0, ${dtmfSequenceToPlay}`;
  showNotify({ type: 'info', message: `开始音频拨号: ${finalSequenceForDisplay}`, duration: (300 + 200) + (dtmfSequenceToPlay.length * (300 + 150)) - (dtmfSequenceToPlay.length > 0 ? 150 : 0) });
  try {
    if (process.env.NODE_ENV === 'development') console.log(`Playing Audio Dial Prefix: 0`);
    await playSingleDtmfTone('0', 300);
    await new Promise(resolve => setTimeout(resolve, 200));
    for (let i = 0; i < dtmfSequenceToPlay.length; i++) {
      const char = dtmfSequenceToPlay[i];
      if (process.env.NODE_ENV === 'development') console.log(`Playing Audio Dial Char: ${char}`);
      await playSingleDtmfTone(char, 300);
      if (i < dtmfSequenceToPlay.length - 1) { await new Promise(resolve => setTimeout(resolve, 150)); }
    }
    showNotify({type: 'success', message: '音频拨号播放完毕'});
    if (process.env.NODE_ENV === 'development') console.log("Audio Dial sequence finished.");
  } catch (error) {
      console.error(`Error playing audio dial sequence:`, error);
      showNotify({type: 'danger', message: `音频拨号序列播放失败`});
  } finally {
    isDtmfPlaying.value = false; dtmfPlayingForOriginal.value = '';
  }
};

const durationValidator = (val) => {
  if (val === null || val === '') return false;
  const num = Number(val);
  return Number.isInteger(num) && num >= 0;
};

const onSubmit = () => {
  console.log('ContactModal: onSubmit function CALLED.');
  submitLoading.value = true;
  const dataToEmit = {
    librarian_id: formData.librarian_id,
    call_duration_minutes: formData.call_duration_minutes,
    notes: formData.notes,
    health_status_recorded: formData.health_status_recorded,
  };

  if (!props.editingLog) { // This is for new records
    dataToEmit.contacted_phone_number = recentlyDialedPhoneNumber.value;
    // **** ADD contact_timestamp FOR NEW LOGS ****
    if (contactStartTime.value) {
      // 将本地时间转换为UTC时间戳
      dataToEmit.contact_timestamp = Math.floor(contactStartTime.value.getTime() / 1000);
      
      // 如果需要调试时区问题，可以启用以下日志
      if (process.env.NODE_ENV === 'development') {
        console.log('ContactModal: 记录本地时间:', contactStartTime.value);
        console.log('ContactModal: 记录UTC时间戳:', dataToEmit.contact_timestamp);
        console.log('ContactModal: 对应的UTC时间:', new Date(dataToEmit.contact_timestamp * 1000).toUTCString());
      }
    } else {
      // Fallback if contactStartTime somehow isn't set, though it should be for new logs.
      // 使用当前UTC时间戳
      console.warn('ContactModal: contactStartTime was not set for a new log, using current time for contact_timestamp.');
      dataToEmit.contact_timestamp = Math.floor(new Date().getTime() / 1000);
    }
  }
  
  if (props.editingLog && props.editingLog.id) {
      dataToEmit.log_id = props.editingLog.id;
      // For editing, contact_timestamp is usually not changed.
      // If it needs to be editable, it should be part of props.editingLog and formData.
      // The backend currently expects contact_timestamp only for new logs based on its validation.
      // If backend update also takes contact_timestamp, this might need adjustment.
      // For now, only adding for new logs as per the 400 error for missing field on create.
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: formData before emit:', JSON.parse(JSON.stringify(dataToEmit)));
    console.log('ContactModal: About to emit submit event.');
  }
  emit('submit', dataToEmit);
  if (process.env.NODE_ENV === 'development') {
    console.log('ContactModal: Submit event emitted with data:', JSON.parse(JSON.stringify(dataToEmit)));
  }
  // submitLoading 通常由父组件在API调用后，通过关闭弹窗或特定prop来重置
};

</script>

<style scoped>
/* STYLE 部分与上一条您确认的【完整 ContactModal.vue】中的【完全一致】 */
.contact-modal-content { display: flex; flex-direction: column; background-color: #f7f8fa; min-height: 300px; height: 100%; box-sizing: border-box; padding-bottom: calc(20px + env(safe-area-inset-bottom)); }
.modal-navbar .van-nav-bar__title { font-weight: bold; font-size: var(--van-font-size-lg); }
.modal-navbar .van-icon { color: var(--van-primary-color); }
.phone-cell-item .van-cell__title, .phone-cell-item .van-button { font-size: var(--van-font-size-md); }
.dial-button {} /* 可以移除，如果 action-icon-button 已足够 */
.contact-form { flex-grow: 1; overflow-y: auto; } /* 确保表单可滚动，而不是整个弹窗内容 */
.form-cell-group .van-field__label { font-weight: 500; font-size: var(--van-font-size-md); }
.form-cell-group .van-field__control, .form-cell-group .van-field__placeholder { font-size: var(--van-font-size-md); }
.health-radio-group .van-radio__label { font-size: var(--van-font-size-md); }
.submit-button { font-weight: bold; font-size: var(--van-font-size-lg); }
.van-nav-bar { flex-shrink: 0; }
.phone-actions { display: flex; align-items: center; }
.dtmf-button .van-loading { margin-right: 0; display: inline-flex; align-items: center; justify-content: center; height: 100%; }
.dtmf-playing-notice, .contact-timer-notice { padding: 8px 16px; background-color: #fffbe8; color: #ed6a0c; font-size: var(--van-font-size-sm); text-align: center; border-bottom: 1px solid #f0f0f0; display: flex; align-items: center; justify-content: center; }
.contact-timer-notice { background-color: #ecf9ff; color: var(--van-primary-color); }
.contact-timer-notice.estimating .van-loading { font-size: 16px; }
.dtmf-playing-notice .van-icon { margin-right: 5px; vertical-align: middle; }
.action-icon-button { padding: 0 8px; min-width: 36px; height: 32px; line-height: 32px; }
.action-icon-button .van-icon { font-size: 20px; }
</style>